import * as React from 'react'
import { Card } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import { RewardSlipResponse } from 'api/common/responses'
import { RewardPosition } from 'api/common/types'
import { Status } from 'api/common/statuses'
import { rewardSlipAPI } from 'api/reward'
import Footer from 'components/Footer'
import Header from 'components/Header'
import InputSelectField from 'components/InputSelectField'
import loginData from 'utilities/loginData'
import { getPositionLabel, KeyValue } from 'utilities/resources/options'
import { dateFormatter, numberFormat } from 'utilities/typeUtility'

/**
 * 報酬明細
 */
const SceneComponent: React.FC = () => {
  const navigate = useNavigate()
  const [targetMonth, setTargetMonth] = React.useState<string>('000000')
  const [selectableMonths, setSelectableMonths] = React.useState<KeyValue[]>([])
  const [titleName, setTitleName] = React.useState<string>()
  const [rewardPositions, setRewardPositions] = React.useState<RewardPosition[]>()
  const [totalUnilevelBonus, setTotalUnilevelBonus] = React.useState<number>(0)
  const [totalInfinityBonus, setTotalInfinityBonus] = React.useState<number>(0)
  const [totalDirectBonus, setTotalDirectBonus] = React.useState<number>(0)
  const [totalBinaryBonus, setTotalBinaryBonus] = React.useState<number>(0)
  const [totalMegaMatchBonus, setTotalMegaMatchBonus] = React.useState<number>(0)
  const [totalAllocationBonus, setTotalAllocationBonus] = React.useState<number>(0)
  const [bonusAmount, setBonusAmount] = React.useState<number>(0)
  const [includedTax, setIncludedTax] = React.useState<number>(0)
  const [transferFee, setTransferFee] = React.useState<number>(0)
  const [transferAmount, setTransferAmount] = React.useState<number>(0)
  const [deposit, setDeposit] = React.useState<number>(0)
  const [adjustmentAmount, setAdjustmentAmount] = React.useState<number>(0)
  const [campaignReward, setCampaignReward] = React.useState<number>(0)
  const [found, setFound] = React.useState<boolean>(false)
  React.useEffect(() => {
    if (!loginData.isLoggedIn()) {
      navigate('/')
    }
  }, [navigate])
  React.useEffect(() => {
    rewardSlipAPI(targetMonth, (response?: RewardSlipResponse, status?: Status) => {
      if (status === Status.Success && response) {
        setFound(true)
        setTitleName(response.titleName)
        setRewardPositions(response.rewardPositions)
        let totalUniLevelBonus = 0,
          totalInfinityBonus = 0,
          totalDirectBonus = 0,
          totalBinaryBonus = 0,
          totalMegaMatchBonus = 0,
          totalAllocationBonus = 0,
          bonusAmount = 0,
          includedTax = 0,
          transferFee = 0,
          transferAmount = 0,
          deposit = 0,
          adjustmentAmount = 0,
          campaignReward = 0
        for (const rewardPosition of response.rewardPositions) {
          totalUniLevelBonus += rewardPosition.unilevelBonus
          totalInfinityBonus += rewardPosition.infinityBonus
          totalDirectBonus += rewardPosition.directBonus
          totalBinaryBonus += rewardPosition.binaryBonus
          totalMegaMatchBonus += rewardPosition.megaMatchBonus
          totalAllocationBonus += rewardPosition.allocationBonus
          bonusAmount += rewardPosition.bonusAmount
          includedTax += rewardPosition.bonusAmountIncludedTax
          transferFee += rewardPosition.transferFee
          transferAmount += rewardPosition.transferAmount
          deposit += rewardPosition.nextDeposit
          adjustmentAmount += rewardPosition.adjustmentAmount
          campaignReward += rewardPosition.campaignReward
        }
        setTotalUnilevelBonus(totalUniLevelBonus)
        setTotalInfinityBonus(totalInfinityBonus)
        setTotalDirectBonus(totalDirectBonus)
        setTotalBinaryBonus(totalBinaryBonus)
        setTotalMegaMatchBonus(totalMegaMatchBonus)
        setTotalAllocationBonus(totalAllocationBonus)
        setBonusAmount(bonusAmount)
        setIncludedTax(includedTax)
        setTransferFee(transferFee)
        setTransferAmount(transferAmount)
        setDeposit(deposit)
        setAdjustmentAmount(adjustmentAmount)
        setCampaignReward(campaignReward)
      } else {
        setFound(false)
      }

      if (response?.targetMonths && response.targetMonths.length > 0) {
        const selectableMonths: KeyValue[] = []
        for (const targetMonth of response.targetMonths) {
          const matches = targetMonth.match(/^(\d{4})(\d{2})$/)
          if (matches) {
            const date = new Date(`${matches[1]}/${matches[2]}/01`)
            selectableMonths.push({
              key: targetMonth,
              value: dateFormatter(date, 'yyyy年M月'),
            })
          }
        }
        setSelectableMonths(selectableMonths)
      }
    })
  }, [targetMonth])

  return (
    <>
      <Header title="報酬明細" />
      <main>
        <section className="mx-4 my-5">
          <InputSelectField name="team" label="報酬集計対象期間" items={selectableMonths} bold className="mt-1" setValue={setTargetMonth} />
          {found && (
            <Card body className="border-0 mt-3">
              <h3 className="fw-bold bg-black text-white rounded text-center py-3">{titleName}</h3>
              {rewardPositions?.map(({ positionNumber, unilevelBonus, infinityBonus, directBonus, binaryBonus, megaMatchBonus, allocationBonus }, index) => (
                <div className="mt-5" key={index}>
                  <h3 className="fw-bold">{getPositionLabel(positionNumber)}</h3>
                  <div className="d-flex justify-content-between py-3 border-bottom">
                    <div>ユニレベルボーナス</div>
                    <div>￥ {numberFormat(unilevelBonus)}</div>
                  </div>
                  <div className="d-flex justify-content-between py-3 border-bottom">
                    <div>インフィニティボーナス</div>
                    <div>￥ {numberFormat(infinityBonus)}</div>
                  </div>
                  <div className="d-flex justify-content-between py-3 border-bottom">
                    <div>ダイレクトボーナス</div>
                    <div>￥ {numberFormat(directBonus)}</div>
                  </div>
                  <div className="d-flex justify-content-between py-3 border-bottom">
                    <div>バイナリーボーナス</div>
                    <div>￥ {numberFormat(binaryBonus)}</div>
                  </div>
                  <div className="d-flex justify-content-between py-3 border-bottom">
                    <div>メガマッチボーナス</div>
                    <div>￥ {numberFormat(megaMatchBonus)}</div>
                  </div>
                  <div className="d-flex justify-content-between py-3 border-bottom">
                    <div>アロケーションボーナス</div>
                    <div>￥ {numberFormat(allocationBonus)}</div>
                  </div>
                </div>
              ))}
              <div className="mt-5">
                <h3 className="fw-bold">振替報酬額</h3>
                <div className="d-flex justify-content-between py-3 border-bottom">
                  <div>ユニレベルボーナス</div>
                  <div>￥ {numberFormat(totalUnilevelBonus)}</div>
                </div>
                <div className="d-flex justify-content-between py-3 border-bottom">
                  <div>インフィニティボーナス</div>
                  <div>￥ {numberFormat(totalInfinityBonus)}</div>
                </div>
                <div className="d-flex justify-content-between py-3 border-bottom">
                  <div>ダイレクトボーナス</div>
                  <div>￥ {numberFormat(totalDirectBonus)}</div>
                </div>
                <div className="d-flex justify-content-between py-3 border-bottom">
                  <div>バイナリーボーナス</div>
                  <div>￥ {numberFormat(totalBinaryBonus)}</div>
                </div>
                <div className="d-flex justify-content-between py-3 border-bottom">
                  <div>メガマッチボーナス</div>
                  <div>￥ {numberFormat(totalMegaMatchBonus)}</div>
                </div>
                <div className="d-flex justify-content-between py-3 border-bottom">
                  <div>アロケーションボーナス</div>
                  <div>￥ {numberFormat(totalAllocationBonus)}</div>
                </div>
              </div>
              <div className="mt-3">
                <div className="d-flex justify-content-between py-2">
                  <div>報酬合計</div>
                  <div>￥ {numberFormat(bonusAmount)}</div>
                </div>
                <div className="d-flex justify-content-between py-2">
                  <div>調整金</div>
                  <div>￥ {numberFormat(adjustmentAmount)}</div>
                </div>
                <div className="d-flex justify-content-between py-2">
                  <div>キャンペーン報酬</div>
                  <div>￥ {numberFormat(campaignReward)}</div>
                </div>
                <div className="d-flex justify-content-between py-2">
                  <div>内、消費税(10%)</div>
                  <div>￥ {numberFormat(includedTax)}</div>
                </div>
                <div className="d-flex justify-content-between py-2">
                  <div>振込手数料</div>
                  <div>￥ {numberFormat(transferFee)}</div>
                </div>
                <div className="d-flex justify-content-between py-2">
                  <div>振込金額</div>
                  <div>￥ {numberFormat(transferAmount)}</div>
                </div>
                <div className="d-flex justify-content-between py-2">
                  <div>他、預り金</div>
                  <div>￥ {numberFormat(deposit)}</div>
                </div>
                <div className="border-bottom"></div>
              </div>
            </Card>
          )}
          {!found && (
            <Card body className="border-0 mt-3">
              <div className="text-center">報酬明細はありません</div>
            </Card>
          )}
        </section>
      </main>
      <Footer className="mt-5" />
    </>
  )
}

export default SceneComponent
